import React, { useMemo, useState } from 'react';
import {
    Alert, AlertIcon, Box, Button, Flex, FormControl, FormErrorMessage, FormHelperText, FormLabel, HStack, Icon,
    Input, InputGroup, InputRightAddon, SimpleGrid, Table, Tbody, Td, Text, Th, Thead, Tr, VStack,
} from '@chakra-ui/react';
import { FaBolt, FaClock, FaHandHoldingUsd, FaUndo } from 'react-icons/fa';
import {
    LIMITES, MAXIMO_LALAMOVE, MINIMO_LALAMOVE, MODOS, PADRAO, brl, descrever, linhaDoTempo, normalizar, pct,
} from './regras';

const ICONES = { normal: FaClock, prioridade: FaBolt };

const AJUDA = {
    inicial: 'Somado assim que a corrida é pedida. No normal costuma ser 0.',
    intervalo_min: 'Quanto tempo sem motorista até o próximo aumento.',
    aumento: 'Quanto soma a cada intervalo.',
    teto: 'Acréscimo máximo. A corrida nunca passa disso.',
};

function foraDoLimite(campo, v) {
    const n = Number(String(v).replace(',', '.'));
    const lim = LIMITES[campo];
    return v === '' || v === null || v === undefined || !Number.isFinite(n) || n < lim.min || n > lim.max;
}

function ColunaModo({ modo, regra, onCampo, original }) {
    const { titulo, subtitulo, cor } = MODOS[modo];
    const valida = Object.keys(LIMITES).every((c) => !foraDoLimite(c, regra[c]));
    const pontos = useMemo(
        () => (valida ? linhaDoTempo(original, normalizar({ [modo]: regra })[modo], 6) : []),
        [valida, original, modo, regra],
    );

    return (
        <Box borderWidth="1px" borderRadius="xl" p={{ base: 3, md: 4 }} borderColor={`${cor}.200`} bg="white">
            <HStack spacing={2} mb={1}>
                <Flex boxSize="28px" borderRadius="full" align="center" justify="center" bg={`${cor}.500`} color="white">
                    <Icon as={ICONES[modo]} boxSize={3} />
                </Flex>
                <VStack align="start" spacing={0}>
                    <Text fontWeight="bold" fontSize="sm">{titulo}</Text>
                    <Text fontSize="xs" color="gray.500">{subtitulo}</Text>
                </VStack>
            </HStack>
            {valida && (
                <Text fontSize="xs" color="gray.600" mb={3}>
                    {descrever(normalizar({ [modo]: regra })[modo])}.
                </Text>
            )}

            <SimpleGrid columns={2} spacing={3}>
                {Object.entries(LIMITES).map(([campo, lim]) => {
                    const invalido = foraDoLimite(campo, regra[campo]);
                    return (
                        <FormControl key={campo} isInvalid={invalido}>
                            <FormLabel fontSize="xs" color="gray.600" fontWeight="bold" mb={1} noOfLines={1}>{lim.curto || lim.rotulo}</FormLabel>
                            <InputGroup size="sm">
                                <Input
                                    type="text"
                                    inputMode="decimal"
                                    borderRadius="lg"
                                    value={regra[campo] ?? ''}
                                    onChange={(e) => onCampo(campo, e.target.value.replace(/[^0-9.,]/g, ''))}
                                    aria-label={`${titulo}: ${lim.rotulo}`}
                                    h="38px"
                                />
                                <InputRightAddon h="38px" borderRightRadius="lg" fontSize="xs" px={2}>{lim.sufixo}</InputRightAddon>
                            </InputGroup>
                            {invalido ? (
                                <FormErrorMessage fontSize="xs">Entre {lim.min} e {lim.max}</FormErrorMessage>
                            ) : (
                                <FormHelperText fontSize="10px" color="gray.400" display={{ base: 'none', md: 'block' }}>
                                    {AJUDA[campo]}
                                </FormHelperText>
                            )}
                        </FormControl>
                    );
                })}
            </SimpleGrid>

            {pontos.length > 0 && (
                <Box mt={4}>
                    <Text fontSize="xs" fontWeight="bold" color="gray.600" mb={1}>
                        Corrida de {brl(original)}, se ninguém aceitar
                    </Text>
                    <Table size="sm" variant="simple">
                        <Thead>
                            <Tr>
                                <Th px={1} fontSize="10px">Tempo</Th>
                                <Th px={1} fontSize="10px" isNumeric>Gorjeta</Th>
                                <Th px={1} fontSize="10px" isNumeric>Total</Th>
                            </Tr>
                        </Thead>
                        <Tbody>
                            {pontos.map((p) => (
                                <React.Fragment key={p.minuto}>
                                {p.pulou && (
                                    <Tr>
                                        <Td px={1} py={0} fontSize="xs" color="gray.400" colSpan={3} borderBottomWidth={0}>⋯</Td>
                                    </Tr>
                                )}
                                <Tr>
                                    <Td px={1} fontSize="xs">{p.minuto === 0 ? 'No pedido' : `${p.minuto} min`}</Td>
                                    <Td px={1} fontSize="xs" isNumeric>
                                        +{brl(p.gorjeta)}{' '}
                                        <Text as="span" color={p.ajuste ? 'orange.500' : 'gray.400'}>
                                            ({p.ajuste === 'minimo' ? 'mín.' : p.ajuste === 'maximo' ? 'máx.' : pct(p.percentual)})
                                        </Text>
                                    </Td>
                                    <Td px={1} fontSize="xs" isNumeric fontWeight="bold">{brl(p.total)}</Td>
                                </Tr>
                                </React.Fragment>
                            ))}
                        </Tbody>
                    </Table>
                </Box>
            )}
        </Box>
    );
}

/**
 * Configuracao do aumento automatico da gorjeta (ticket #2305).
 *
 * Os campos ficam como texto enquanto o operador digita (aceita virgula e
 * campo vazio no meio da edicao); quem converte e valida e o `conferir` antes
 * de salvar, e o servidor de novo. A tabela de exemplo mostra o efeito em
 * reais de cada mudanca, antes de salvar.
 */
export default function ConfigGorjeta({ valor, onChange, avisos = [] }) {
    const [exemplo, setExemplo] = useState('40');
    const original = Math.max(1, Number(String(exemplo).replace(',', '.')) || 40);

    const regras = valor || PADRAO;
    const igualAoPadrao = JSON.stringify(normalizar(regras)) === JSON.stringify(normalizar(PADRAO));

    const alterar = (modo, campo, v) => onChange({ ...regras, [modo]: { ...regras[modo], [campo]: v } });

    return (
        <Box>
            <Flex justify="space-between" align={{ base: 'start', md: 'center' }} gap={3} mb={1} direction={{ base: 'column', md: 'row' }}>
                <HStack spacing={2}>
                    <Icon as={FaHandHoldingUsd} color="gray.400" boxSize={3} />
                    <Text fontSize="sm" fontWeight="bold" color="gray.700">Aumento automático da gorjeta</Text>
                </HStack>
                <HStack spacing={2} w={{ base: '100%', md: 'auto' }}>
                    <FormControl w={{ base: '50%', md: '150px' }}>
                        <InputGroup size="sm">
                            <Input
                                type="text"
                                inputMode="decimal"
                                value={exemplo}
                                onChange={(e) => setExemplo(e.target.value.replace(/[^0-9.,]/g, ''))}
                                borderRadius="lg"
                                aria-label="Valor de exemplo da corrida"
                            />
                            <InputRightAddon fontSize="xs" px={2} borderRightRadius="lg">exemplo R$</InputRightAddon>
                        </InputGroup>
                    </FormControl>
                    <Button
                        size="sm"
                        variant="ghost"
                        leftIcon={<Icon as={FaUndo} boxSize={3} />}
                        onClick={() => onChange(JSON.parse(JSON.stringify(PADRAO)))}
                        isDisabled={igualAoPadrao}
                        w={{ base: '50%', md: 'auto' }}
                    >
                        Usar sugestão
                    </Button>
                </HStack>
            </Flex>
            <Text fontSize="xs" color="gray.400" mb={3}>
                Enquanto a Lalamove procura motorista, a gorjeta sobe sozinha e para assim que alguém aceita.
                Os percentuais são sempre sobre o preço original da corrida. Quem pede a corrida escolhe o modo
                na hora de confirmar. Mudanças valem para as próximas corridas. A Lalamove só aceita gorjeta de
                {' '}{brl(MINIMO_LALAMOVE)} a {brl(MAXIMO_LALAMOVE)}: abaixo disso o aumento sai como {brl(MINIMO_LALAMOVE)} (mín.),
                acima fica em {brl(MAXIMO_LALAMOVE)} (máx.).
            </Text>

            <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={3}>
                {['normal', 'prioridade'].map((modo) => (
                    <ColunaModo
                        key={modo}
                        modo={modo}
                        regra={regras[modo] || PADRAO[modo]}
                        original={original}
                        onCampo={(campo, v) => alterar(modo, campo, v)}
                    />
                ))}
            </SimpleGrid>

            {avisos.length > 0 && (
                <VStack align="stretch" spacing={2} mt={3}>
                    {avisos.map((a) => (
                        <Alert key={a} status="warning" borderRadius="lg" fontSize="xs" py={2}>
                            <AlertIcon boxSize={4} />{a}
                        </Alert>
                    ))}
                </VStack>
            )}
        </Box>
    );
}
